import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSupplyChain } from "./hooks/useSupplyChain";
import { STATUS_LABELS, CONTRACT_ADDRESS } from "./contracts/config";
import "./App.css";

export default function App() {
    const {
        account, isConnected, isLoading, error,
        connectWallet, createProduct, updateStatus,
        getProduct, getTotalProducts, recallProduct, addQualityCheck
    } = useSupplyChain();

    // ── State ──────────────────────────────────────────
    const [activeTab,     setActiveTab]     = useState("track");
    const [totalProducts, setTotalProducts] = useState(0);

    // Create product form
    const [createForm, setCreateForm] = useState({
        name: "", batchNumber: "", price: ""
    });

    // Track product
    const [trackId,      setTrackId]      = useState("");
    const [trackedProduct, setTrackedProduct] = useState(null);

    // Update status form
    const [updateForm, setUpdateForm] = useState({
        productId: "", status: "1", location: "", notes: ""
    });

    // Quality check form
    const [qcForm, setQcForm] = useState({
        productId: "", result: ""
    });

    // Recall form
    const [recallForm, setRecallForm] = useState({
        productId: "", reason: ""
    });

    // ── Load total products when connected ─────────────
    useEffect(() => {
        if (isConnected) {
            getTotalProducts().then(setTotalProducts);
        }
    }, [isConnected, getTotalProducts]);

    // ── Handlers ───────────────────────────────────────
    async function handleCreateProduct(e) {
        e.preventDefault();
        const result = await createProduct(
            createForm.name,
            createForm.batchNumber,
            createForm.price
        );
        if (result.success) {
            toast.success("✅ Product created on blockchain!");
            setCreateForm({ name: "", batchNumber: "", price: "" });
            getTotalProducts().then(setTotalProducts);
        } else {
            toast.error("❌ " + result.error);
        }
    }

    async function handleTrackProduct() {
        if (!trackId) return;
        const result = await getProduct(trackId);
        if (result.success) {
            setTrackedProduct(result);
        } else {
            toast.error("Product not found");
            setTrackedProduct(null);
        }
    }

    async function handleUpdateStatus(e) {
        e.preventDefault();
        const result = await updateStatus(
            updateForm.productId,
            parseInt(updateForm.status),
            updateForm.location,
            updateForm.notes
        );
        if (result.success) {
            toast.success("✅ Status updated on blockchain!");
            setUpdateForm({ productId: "", status: "1", location: "", notes: "" });
        } else {
            toast.error("❌ " + result.error);
        }
    }

    async function handleAddQC(e) {
        e.preventDefault();
        const result = await addQualityCheck(qcForm.productId, qcForm.result);
        if (result.success) {
            toast.success("✅ Quality check recorded!");
            setQcForm({ productId: "", result: "" });
        } else {
            toast.error("❌ " + result.error);
        }
    }

    async function handleRecall(e) {
        e.preventDefault();
        const result = await recallProduct(recallForm.productId, recallForm.reason);
        if (result.success) {
            toast.success("⚠️ Product recalled!");
            setRecallForm({ productId: "", reason: "" });
        } else {
            toast.error("❌ " + result.error);
        }
    }

    // ── Render ─────────────────────────────────────────
    return (
        <div className="app">
            <ToastContainer position="top-right" autoClose={4000} />

            {/* Header */}
            <header className="header">
                <div className="header-left">
                    <h1>⛓️ LogiChain</h1>
                    <span className="subtitle">Powered by AxiQuant AI</span>
                </div>
                <div className="header-right">
                    {isConnected ? (
                        <div className="wallet-info">
                            <span className="dot green"></span>
                            <span className="address">
                                {account.slice(0,6)}...{account.slice(-4)}
                            </span>
                            <a
                                href={`https://sepolia.etherscan.io/address/${CONTRACT_ADDRESS}`}
                                target="_blank"
                                rel="noreferrer"
                                className="etherscan-link"
                            >
                                View Contract ↗
                            </a>
                        </div>
                    ) : (
                        <button
                            className="btn btn-primary"
                            onClick={connectWallet}
                            disabled={isLoading}
                        >
                            {isLoading ? "Connecting..." : "🦊 Connect MetaMask"}
                        </button>
                    )}
                </div>
            </header>

            {/* Not connected screen */}
            {!isConnected && (
                <div className="connect-screen">
                    <div className="connect-card">
                        <div className="connect-icon">⛓️</div>
                        <h2>⛓️ LogiChain</h2>
                        <p>Track products from manufacturer to customer on Ethereum blockchain</p>
                        <ul className="features">
                            <li>✅ Tamper-proof product tracking</li>
                            <li>✅ Role-based access control</li>
                            <li>✅ Real-time status updates</li>
                            <li>✅ Quality check records</li>
                            <li>✅ Product recall system</li>
                        </ul>
                        <button
                            className="btn btn-primary btn-large"
                            onClick={connectWallet}
                            disabled={isLoading}
                        >
                            {isLoading ? "Connecting..." : "🦊 Connect MetaMask to Start"}
                        </button>
                        {error && <p className="error">{error}</p>}
                    </div>
                </div>
            )}

            {/* Main content */}
            {isConnected && (
                <main className="main">
                    {/* Stats bar */}
                    <div className="stats-bar">
                        <div className="stat">
                            <span className="stat-value">{totalProducts}</span>
                            <span className="stat-label">Total Products</span>
                        </div>
                        <div className="stat">
                            <span className="stat-value">Sepolia</span>
                            <span className="stat-label">Network</span>
                        </div>
                        <div className="stat">
                            <span className="stat-value">Live</span>
                            <span className="stat-label">Contract Status</span>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="tabs">
                        {[
                            { id: "track",   label: "🔍 Track Product"   },
                            { id: "create",  label: "📦 Create Product"  },
                            { id: "update",  label: "🔄 Update Status"   },
                            { id: "quality", label: "✔️ Quality Check"   },
                            { id: "recall",  label: "⚠️ Recall Product"  },
                        ].map(tab => (
                            <button
                                key={tab.id}
                                className={`tab ${activeTab === tab.id ? "active" : ""}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Tab Content */}
                    <div className="tab-content">

                        {/* TRACK */}
                        {activeTab === "track" && (
                            <div className="panel">
                                <h2>🔍 Track Product</h2>
                                <p className="panel-desc">Enter a product ID to see its full supply chain journey</p>
                                <div className="search-row">
                                    <input
                                        type="number"
                                        placeholder="Enter Product ID (e.g. 1)"
                                        value={trackId}
                                        onChange={e => setTrackId(e.target.value)}
                                        className="input"
                                        min="1"
                                    />
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleTrackProduct}
                                        disabled={isLoading || !trackId}
                                    >
                                        {isLoading ? "Loading..." : "Track"}
                                    </button>
                                </div>

                                {trackedProduct && (
                                    <div className="product-detail">
                                        {/* Product Info */}
                                        <div className="product-card">
                                            <div className="product-header">
                                                <h3>{trackedProduct.product.name}</h3>
                                                <span
                                                    className="status-badge"
                                                    style={{ background: STATUS_LABELS[Number(trackedProduct.product.status)]?.color }}
                                                >
                                                    {STATUS_LABELS[Number(trackedProduct.product.status)]?.icon}
                                                    {STATUS_LABELS[Number(trackedProduct.product.status)]?.label}
                                                </span>
                                            </div>
                                            <div className="product-meta">
                                                <div className="meta-item">
                                                    <span className="meta-label">Product ID</span>
                                                    <span className="meta-value">#{Number(trackedProduct.product.id)}</span>
                                                </div>
                                                <div className="meta-item">
                                                    <span className="meta-label">Batch</span>
                                                    <span className="meta-value">{trackedProduct.product.batchNumber}</span>
                                                </div>
                                                <div className="meta-item">
                                                    <span className="meta-label">Manufacturer</span>
                                                    <span className="meta-value mono">
                                                        {trackedProduct.product.manufacturer.slice(0,8)}...
                                                    </span>
                                                </div>
                                                <div className="meta-item">
                                                    <span className="meta-label">Price</span>
                                                    <span className="meta-value">
                                                        {/* ethers v6 formatEther */}
                                                        {(Number(trackedProduct.product.price) / 1e18).toFixed(4)} ETH
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Timeline */}
                                        <h3 className="section-title">📍 Tracking History</h3>
                                        <div className="timeline">
                                            {trackedProduct.history.map((record, i) => (
                                                <div key={i} className="timeline-item">
                                                    <div className="timeline-dot"
                                                        style={{ background: STATUS_LABELS[Number(record.status)]?.color }}>
                                                        {STATUS_LABELS[Number(record.status)]?.icon}
                                                    </div>
                                                    <div className="timeline-content">
                                                        <div className="timeline-header">
                                                            <strong>{STATUS_LABELS[Number(record.status)]?.label}</strong>
                                                            <span className="timeline-time">
                                                                {new Date(Number(record.timestamp) * 1000).toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <p className="timeline-location">📍 {record.location}</p>
                                                        <p className="timeline-notes">{record.notes}</p>
                                                        <p className="timeline-by">
                                                            By: {record.updatedBy.slice(0,8)}...
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Quality Checks */}
                                        {trackedProduct.checks.length > 0 && (
                                            <>
                                                <h3 className="section-title">✔️ Quality Checks</h3>
                                                <div className="qc-list">
                                                    {trackedProduct.checks.map((check, i) => (
                                                        <div key={i} className="qc-item">
                                                            <span className="qc-number">#{i+1}</span>
                                                            <span>{check}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* CREATE */}
                        {activeTab === "create" && (
                            <div className="panel">
                                <h2>📦 Create Product</h2>
                                <p className="panel-desc">Register a new product on the blockchain (Manufacturer only)</p>
                                <form onSubmit={handleCreateProduct} className="form">
                                    <div className="form-group">
                                        <label>Product Name</label>
                                        <input
                                            className="input"
                                            placeholder="e.g. iPhone 15 Pro"
                                            value={createForm.name}
                                            onChange={e => setCreateForm({...createForm, name: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Batch Number</label>
                                        <input
                                            className="input"
                                            placeholder="e.g. BATCH-2024-001"
                                            value={createForm.batchNumber}
                                            onChange={e => setCreateForm({...createForm, batchNumber: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Price (ETH)</label>
                                        <input
                                            className="input"
                                            type="number"
                                            step="0.001"
                                            placeholder="e.g. 0.5"
                                            value={createForm.price}
                                            onChange={e => setCreateForm({...createForm, price: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-full"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "⏳ Creating on blockchain..." : "📦 Create Product"}
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* UPDATE STATUS */}
                        {activeTab === "update" && (
                            <div className="panel">
                                <h2>🔄 Update Product Status</h2>
                                <p className="panel-desc">Move product to next stage in supply chain</p>
                                <form onSubmit={handleUpdateStatus} className="form">
                                    <div className="form-group">
                                        <label>Product ID</label>
                                        <input
                                            className="input"
                                            type="number"
                                            placeholder="e.g. 1"
                                            value={updateForm.productId}
                                            onChange={e => setUpdateForm({...updateForm, productId: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>New Status</label>
                                        <select
                                            className="input"
                                            value={updateForm.status}
                                            onChange={e => setUpdateForm({...updateForm, status: e.target.value})}
                                        >
                                            <option value="1">🏪 In Warehouse</option>
                                            <option value="2">🚚 In Transit</option>
                                            <option value="3">🏬 At Retailer</option>
                                            <option value="4">✅ Delivered</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Location</label>
                                        <input
                                            className="input"
                                            placeholder="e.g. Mumbai Warehouse"
                                            value={updateForm.location}
                                            onChange={e => setUpdateForm({...updateForm, location: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Notes</label>
                                        <input
                                            className="input"
                                            placeholder="e.g. Received in good condition"
                                            value={updateForm.notes}
                                            onChange={e => setUpdateForm({...updateForm, notes: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-full"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "⏳ Updating on blockchain..." : "🔄 Update Status"}
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* QUALITY CHECK */}
                        {activeTab === "quality" && (
                            <div className="panel">
                                <h2>✔️ Add Quality Check</h2>
                                <p className="panel-desc">Record quality inspection results (Manufacturer only)</p>
                                <form onSubmit={handleAddQC} className="form">
                                    <div className="form-group">
                                        <label>Product ID</label>
                                        <input
                                            className="input"
                                            type="number"
                                            placeholder="e.g. 1"
                                            value={qcForm.productId}
                                            onChange={e => setQcForm({...qcForm, productId: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Result</label>
                                        <input
                                            className="input"
                                            placeholder="e.g. PASSED - No defects found"
                                            value={qcForm.result}
                                            onChange={e => setQcForm({...qcForm, result: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-full"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "⏳ Recording..." : "✔️ Add Quality Check"}
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* RECALL */}
                        {activeTab === "recall" && (
                            <div className="panel">
                                <h2>⚠️ Recall Product</h2>
                                <p className="panel-desc">Permanently recall a product (Admin only)</p>
                                <form onSubmit={handleRecall} className="form">
                                    <div className="form-group">
                                        <label>Product ID</label>
                                        <input
                                            className="input"
                                            type="number"
                                            placeholder="e.g. 1"
                                            value={recallForm.productId}
                                            onChange={e => setRecallForm({...recallForm, productId: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Reason</label>
                                        <input
                                            className="input"
                                            placeholder="e.g. Safety defect found in batch"
                                            value={recallForm.reason}
                                            onChange={e => setRecallForm({...recallForm, reason: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn btn-danger btn-full"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "⏳ Processing..." : "⚠️ Recall Product"}
                                    </button>
                                </form>
                            </div>
                        )}

                    </div>
                </main>
            )}
        </div>
    );
}